// Which routes count as TikTok `ViewContent`, and what they report as.
// ----------------------------------------------------------------------------
// Pure and dependency-free so the mapping is unit-testable without a browser.
//
// Deliberately an ALLOW-LIST, not "every route". ViewContent is a pre-purchase
// intent signal that TikTok's bidding learns from, so it should fire on the
// surfaces that actually sell something — the tools themselves, the public
// demos, pricing, and the landing page an ad clicks into. Account settings,
// auth screens, admin, legal pages and the authed hub are navigation, not
// content; reporting them would dilute the signal with routes no buyer decision
// happens on.

export type TikTokContent = {
  contentId: string;
  contentName: string;
};

// The `(tools)` route group — the products the subscription actually sells.
// `m4np8qz6` (obfuscated admin/plugin hub) and `script-docs` are excluded: they
// are internal surfaces, not things a visitor evaluates before buying.
const TOOL_ROUTES: Record<string, string> = {
  "addon-eup-creator": "Add-on EUP Creator",
  "badge-maker": "AI Badge Maker",
  blinklab: "Blink Lab",
  "blinklab/carcols": "Blink Lab — Carcols",
  "chevron-builder": "Chevron Builder",
  "code-smith": "Code Smith",
  "code-visualizer": "Code Visualizer",
  "creator-convert": "Creator Convert",
  "easy-meta": "Easy Meta",
  "embroidery-tool": "Embroidery Tool",
  "eup-viewer": "EUP Viewer",
  "handling-diff": "Handling Diff",
  "ink-lab": "Ink Lab",
  "item-image": "Asset Art",
  "livery-magic": "Livery Magic",
  "model-forge": "Model Forge",
  "pack-bench": "PackBench",
  paintbooth: "Paint Booth",
  "paintbooth-studio": "Paint Booth Studio",
  resmon: "Resmon Report",
  "uv-studio": "UV Studio",
  "weapon-viewer": "Weapon Designer",
};

// Non-tool surfaces where a purchase decision genuinely happens.
const CONTENT_ROUTES: Record<string, TikTokContent> = {
  "/": { contentId: "home", contentName: "The Workshop — Home" },
  "/subscription": { contentId: "pricing", contentName: "Pricing & Plans" },
  "/paintbooth-demo": { contentId: "paintbooth-demo", contentName: "Paint Booth — Demo" },
  "/paintbooth-studio-demo": {
    contentId: "paintbooth-studio-demo",
    contentName: "Paint Booth Studio — Demo",
  },
  "/showcase": { contentId: "showcase", contentName: "Showcase" },
};

/**
 * Resolve a pathname to the content TikTok should be told about, or null when
 * the route isn't a selling surface.
 *
 * Trailing slashes are normalized so "/uv-studio/" and "/uv-studio" report
 * identically instead of splitting one tool into two content ids.
 */
export function resolveTikTokContent(pathname: string): TikTokContent | null {
  if (!pathname) return null;
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";

  const direct = CONTENT_ROUTES[normalized];
  if (direct) return direct;

  const slug = normalized.startsWith("/") ? normalized.slice(1) : normalized;
  const toolName = TOOL_ROUTES[slug];
  if (toolName) return { contentId: slug, contentName: toolName };

  return null;
}
